import type { SVGProps } from 'react'

/*
  A small set of line icons for navigation and status, drawn in currentColor so the
  sidebar, bottom bar and chips colour them through tokens. Status is never colour alone
  (brief §2.7.4): every chip pairs one of these with a word.
*/

export type IconName =
  | 'home'
  | 'cattle'
  | 'pastures'
  | 'hay'
  | 'map'
  | 'leases'
  | 'tasks'
  | 'records'
  | 'notes'
  | 'reports'
  | 'settings'
  | 'more'
  | 'about'
  | 'online'
  | 'offline'
  | 'pending'
  | 'failed'
  | 'synced'
  | 'lock'
  | 'device'
  | 'warning'
  | 'search'
  | 'sort'
  | 'sortUp'
  | 'sortDown'
  | 'close'

const glyphs: Record<IconName, string> = {
  home: 'M3 11l9-8 9 8v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  cattle:
    'M4 8c-1.5 0-2.5-1-2.5-2.5M20 8c1.5 0 2.5-1 2.5-2.5M6 8h12l2 6v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4zM9 14h.01M15 14h.01',
  pastures: 'M3 20h18M5 20V9l5-4 4 3 5-3v15M8 20v-5M16 20v-6',
  hay: 'M4 18a4 4 0 1 1 8 0 4 4 0 0 1-8 0zm8 0a4 4 0 1 1 8 0 4 4 0 0 1-8 0zM8 10a4 4 0 1 1 8 0 4 4 0 0 1-8 0z',
  map: 'M9 4l6 2 6-2v14l-6 2-6-2-6 2V6zM9 4v14M15 6v14',
  leases: 'M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v6h6M9 13h6M9 17h6',
  tasks: 'M9 6h12M9 12h12M9 18h12M3 6l1.5 1.5L7 5M3 12l1.5 1.5L7 11M3 18l1.5 1.5L7 17',
  records: 'M4 4h12l4 4v12H4zM8 4v5h8M8 14h8M8 18h5',
  // A notepad with a pencil across its corner (Phase 7 notes).
  notes: 'M15 3H4v18h16v-8M8 8h5M8 12h4M8 16h6M13 15l8-8-2-2-8 8v2z',
  reports: 'M4 20V10M10 20V4M16 20v-8M22 20H2',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  about: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01',
  online: 'M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 19.5h.01M2 9a14 14 0 0 1 20 0',
  offline:
    'M2 2l20 20M8.5 16a5 5 0 0 1 7 0M12 19.5h.01M5 12.5a10 10 0 0 1 5.5-2.8M16.5 9.9a10 10 0 0 1 2.5 2.6M2 9a14 14 0 0 1 4.7-3.2M14 5.2A14 14 0 0 1 22 9',
  pending: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 7v5l3 2',
  failed: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM15 9l-6 6M9 9l6 6',
  synced: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12l3 3 5-6',
  lock: 'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1zM8 11V7a4 4 0 0 1 8 0v4',
  device: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM12 18h.01',
  warning: 'M12 3l10 18H2zM12 10v4M12 18h.01',
  // Table toolbars and sortable headings (2026-10-27).
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-5-5',
  sort: 'M8 9l4-4 4 4M8 15l4 4 4-4',
  sortUp: 'M12 19V5M6 11l6-6 6 6',
  sortDown: 'M12 5v14M6 13l6 6 6-6',
  close: 'M6 6l12 12M18 6L6 18',
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  /** Rendered size in em so the icon follows the text size of its container. */
  size?: number
}

export function Icon({ name, size = 1.25, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={`${size}em`}
      height={`${size}em`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d={glyphs[name]} />
    </svg>
  )
}
